/**
 * Shop Design Carousel – FINAL VERSION
 * Fixed: Arrow preventDefault, proper event handling, CTA visibility
 */

(function() {
  'use strict';

  const STOREFRONT_API_URL = '/api/2025-10/graphql.json';
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  const PRODUCTS_PER_PAGE = 50;

  class ShopDesignCarousel {
    constructor(element) {
      this.element = element;
      this.designName = element.dataset.designName;
      this.designGid = element.dataset.designGid;
      this.currentProductId = element.dataset.currentProductId;
      this.productsLimit = parseInt(element.dataset.productsLimit) || 10;
      this.showCta = element.dataset.showCta === 'true';
      this.ctaBgColor = element.dataset.ctaBgColor || '#1caf23';
      this.ctaTextColor = element.dataset.ctaTextColor || '#ffffff';
      this.ctaCategory = element.dataset.ctaCategory || '';
      this.storefrontToken = element.dataset.storefrontToken;

      this.carousel = element.querySelector('.shop-design-carousel');
      this.track = element.querySelector('.shop-design-carousel .shop-design-track');
      this.loading = element.querySelector('.shop-design-carousel__loading');
      this.errorEl = element.querySelector('.shop-design-carousel__error');
      this.ctaEl = element.querySelector('.shop-design-carousel__cta');
      
      this.leftArrow = null;
      this.rightArrow = null;

      this.init();
    }

    async init() {
      console.log('ShopDesignCarousel init started');

      if (!this.designGid && !this.designName) {
        console.log('No designGid or designName – hiding section');
        this.hideSection();
        return;
      }

      try {
        const products = await this.fetchProducts();

        if (products.length === 0) {
          console.log('No products found – hiding section');
          this.hideSection();
          return;
        }

        this.renderProducts(products);
        this.showCarousel();
        this.handleCta();
      } catch (error) {
        console.error('Shop Design Carousel Error:', error);
        this.showError();
      }
    }

    async fetchProducts() {
      const cacheKey = `shop_design_${this.designGid || this.designName}`;
      const cached = this.getCache(cacheKey);
      if (cached) {
        console.log('Using cached products');
        return this.filterAndLimitProducts(cached);
      }

      console.log('Fetching fresh products from API');
      const allProducts = await this.fetchAllProducts();
      this.setCache(cacheKey, allProducts);
      return this.filterAndLimitProducts(allProducts);
    }

    async fetchAllProducts(cursor = null, accumulated = []) {
      const query = `
        query MatchingProducts($first: Int!, $queryString: String!, $after: String) {
          products(first: $first, query: $queryString, sortKey: TITLE, after: $after) {
            edges {
              cursor
              node {
                id
                handle
                title
                productType
                featuredImage {
                  url
                  altText
                }
                onlineStoreUrl
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;

      const variables = {
        queryString: this.designGid
          ? `metafield:custom.meme_design:"${this.designGid}"`
          : `metafield:custom.meme_design:"${this.designName}"`,
        first: PRODUCTS_PER_PAGE,
        after: cursor
      };

      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({ query, variables })
      });

      if (!response.ok) throw new Error(`API request failed: ${response.status}`);
      const data = await response.json();
      if (data.errors) throw new Error(data.errors[0].message);

      const products = data.data.products.edges.map(edge => edge.node);
      const newAccumulated = [...accumulated, ...products];

      if (data.data.products.pageInfo.hasNextPage && newAccumulated.length < this.productsLimit + 20) {
        return this.fetchAllProducts(data.data.products.pageInfo.endCursor, newAccumulated);
      }

      return newAccumulated;
    }

    filterAndLimitProducts(products) {
      return products
        .filter(p => {
          const gidParts = p.id.split('/');
          return gidParts[gidParts.length - 1] !== this.currentProductId;
        })
        .slice(0, this.productsLimit);
    }

    renderProducts(products) {
      console.log('Rendering', products.length, 'products');

      if (!this.track) {
        console.error('ERROR: track element not found');
        return;
      }

      this.track.innerHTML = '';

      products.forEach(product => {
        const card = document.createElement('div');
        card.className = 'shop-design-card';

        const imageUrl = (product.featuredImage && product.featuredImage.url) 
        ? `${product.featuredImage.url}&width=200&height=200&crop=center` 
        : 'https://via.placeholder.com/200x200?text=No+Image';

        const productUrl = product.onlineStoreUrl || `/products/${product.handle}`;
        const type = product.productType ? this.escapeHtml(product.productType) : '';

        card.innerHTML = `
          <a href="${productUrl}">
            <img src="${imageUrl}" alt="${type}" class="shop-design-image" loading="lazy">
          </a>
          ${type ? `<p class="shop-design-type">${type}</p>` : ''}
        `;

        this.track.appendChild(card);
      });

      console.log('Products rendered successfully');
    }

    showCarousel() {
      console.log('Showing carousel');

      this.loading.style.display = 'none';
      this.carousel.style.display = 'block';
      this.carousel.style.visibility = 'visible';
      this.errorEl.style.display = 'none';

      this.setupArrows();
      this.updateArrowVisibility();

      console.log('Carousel visible');
    }

    setupArrows() {
      this.leftArrow = this.carousel.querySelector('.carousel-arrow--left');
      this.rightArrow = this.carousel.querySelector('.carousel-arrow--right');

      if (!this.leftArrow || !this.rightArrow) {
        console.warn('Arrow buttons not found');
        return;
      }

      // CRITICAL: Prevent default on click to stop form submission
      this.leftArrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.scrollLeft();
      });

      this.rightArrow.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.scrollRight();
      });

      // Update arrow state on scroll
      this.track.addEventListener('scroll', () => this.updateArrowVisibility(), { passive: true });
    }

    scrollLeft() {
      const scrollAmount = this.getScrollAmount();
      this.track.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }

    scrollRight() {
      const scrollAmount = this.getScrollAmount();
      this.track.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }

    getScrollAmount() {
      // Calculate scroll amount based on visible cards
      const trackWidth = this.track.offsetWidth;
      const firstCard = this.track.querySelector('.shop-design-card');
      
      if (!firstCard) return trackWidth;

      const cardWidth = firstCard.offsetWidth;
      const gap = parseInt(getComputedStyle(this.track).gap) || 12;
      const cardWithGap = cardWidth + gap;
      
      // Calculate how many cards fit in the viewport
      const cardsVisible = Math.floor(trackWidth / cardWithGap);
      
      // Scroll by the number of visible cards (minimum 1, maximum 4)
      const cardsToScroll = Math.max(1, Math.min(cardsVisible, 4));
      
      return cardsToScroll * cardWithGap;
    }

    updateArrowVisibility() {
      if (!this.leftArrow || !this.rightArrow) return;

      const isAtStart = this.track.scrollLeft <= 1;
      const isAtEnd = this.track.scrollLeft >= (this.track.scrollWidth - this.track.offsetWidth - 1);

      this.leftArrow.style.display = isAtStart ? 'none' : 'flex';
      this.rightArrow.style.display = isAtEnd ? 'none' : 'flex';
    }

    handleCta() {
      // Show CTA if enabled and category exists
      if (this.showCta && this.ctaCategory && this.ctaEl) {
        console.log('Showing CTA for category:', this.ctaCategory);
        this.ctaEl.style.display = 'block';
      } else if (this.ctaEl) {
        console.log('Hiding CTA (showCta:', this.showCta, 'category:', this.ctaCategory, ')');
        this.ctaEl.style.display = 'none';
      }
    }

    showError() {
      this.loading.style.display = 'none';
      this.carousel.style.display = 'none';
      this.errorEl.style.display = 'block';
      if (this.ctaEl) this.ctaEl.style.display = 'none';
    }

    hideSection() {
      const section = this.element.closest('.shop-design-carousel-section');
      if (section) section.style.display = 'none';
    }

    getCache(key) {
      try {
        const cached = sessionStorage.getItem(key);
        if (!cached) return null;
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp > CACHE_DURATION) {
          sessionStorage.removeItem(key);
          return null;
        }
        return data;
      } catch (e) {
        return null;
      }
    }

    setCache(key, data) {
      try {
        sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
      } catch (e) {
        // quota exceeded
      }
    }

    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  function initCarousels() {
    const carousels = document.querySelectorAll('[data-design-carousel]');
    console.log('Found', carousels.length, 'carousel(s)');
    carousels.forEach(el => new ShopDesignCarousel(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }

  window.ShopDesignCarousel = window.ShopDesignCarousel || {};
  window.ShopDesignCarousel.init = initCarousels;
})();

